import { useState, useContext, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Box, CircularProgress } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

import { StrategyContext } from "../contexts/StrategyContext";

import {
  useAddStrategy,
  useDeleteStrategy,
  useStrategies,
  useUpdateStrategy,
  useCloneStrategy,
} from "../api/strategies";
import { useStrategy } from "../api/strategy";
import { exportMitigationStrategyZip } from "../api/strategyExport";

import { Strategy } from "../types/strategy";
import { ApiResponse } from "../types/api";
import ScenarioSegmentButton from "./ScenarioSegmentButton";
import AddStrategyModal from "./AddStrategyModal";
import EditStrategyModal from "./EditStrategyModal";
import { useAuth } from "../contexts/AuthContext";
import { isViewOnly } from "../utils/permissions";
import {
  ScenarioEmptyState,
  ScenarioList,
  ScenarioPopover,
  ScenarioRow,
  StatusLine,
} from "./scenarioBar/ScenarioPopover";
import { useUndoableDelete } from "./scenarioBar/useUndoableDelete";
import { UndoToast } from "./scenarioBar/UndoToast";
import { SB } from "./scenarioBar/tokens";

/** "No Mitigation -- 8 of 36 tiles complete" → { done: 8, total: 36 }. */
const parseTileProgress = (message: string | undefined | null) => {
  const match = /(\d+)\s+of\s+(\d+)\s+tiles/i.exec(message ?? "");
  if (!match) return null;
  const done = Number(match[1]);
  const total = Number(match[2]);
  if (!total) return null;
  return { done, total };
};

export default function StrategiesDropdown() {
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const { selectedStrategy, setSelectedStrategy } = useContext(StrategyContext);
  const { projectId } = useParams();
  const projectIdNumber = Number(projectId) || 0;
  const queryClient = useQueryClient();
  const { mutateAsync: addStrategy } = useAddStrategy(projectIdNumber);
  const { mutate: deleteStrategy } = useDeleteStrategy(projectIdNumber);
  const { mutate: updateStrategy } = useUpdateStrategy(projectIdNumber);
  const { mutate: cloneStrategy } = useCloneStrategy(projectIdNumber);
  // Id of a just-created strategy (blank or clone) waiting for the refetched
  // list to include it, so it can be auto-selected.
  const [pendingStrategyId, setPendingStrategyId] = useState<string | null>(
    null
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [strategyToEdit, setStrategyToEdit] = useState<Strategy | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const { strategies, error, isLoading: listLoading } = useStrategies(projectIdNumber);
  const { isLoading: computing, status } = useStrategy();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { pending, scheduleDelete, undo, dismiss } = useUndoableDelete();

  const list: Strategy[] = useMemo(() => strategies?.data ?? [], [strategies]);
  const strategiesKey = [projectIdNumber, "strategies"];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStrategySelect = (strategy: Strategy) => {
    handleClose();
    setSelectedStrategy(strategy);
  };

  const handleCloneStrategy = (strategyId: string) => {
    cloneStrategy(strategyId, {
      onSuccess: (response) => {
        const newId = response?.data?.id;
        if (newId != null) setPendingStrategyId(String(newId));
      },
    });
  };

  const handleExportStrategy = (strategyId: string) => {
    exportMitigationStrategyZip({ projectId: projectIdNumber, strategyId })
      .then(({ blob, filename }) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename || `mitigation_strategy_${strategyId}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(blobUrl);
      })
      .catch((err) => {
        const message =
          typeof err?.message === "string"
            ? err.message
            : "Failed to export strategy";
        setSnackbar({
          open: true,
          message,
          severity: "error",
        });
      });
  };

  const handleEditStrategy = (strategy: Strategy) => {
    setStrategyToEdit(strategy);
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (name: string, description: string) => {
    if (strategyToEdit) {
      await updateStrategy({ strategyId: strategyToEdit.id, name, description });
    }
  };

  // Delete removes the row at once (query cache) and offers Undo for six
  // seconds; the request goes out when the window closes. Deleting the
  // current strategy moves the selection to the next one first.
  const handleDeleteStrategy = (strategy: Strategy) => {
    const previous = queryClient.getQueryData<ApiResponse<Strategy[]>>(strategiesKey);
    const remaining = list.filter((s) => s.id !== strategy.id);
    if (selectedStrategy?.id === strategy.id && remaining.length) {
      setSelectedStrategy(remaining[0]);
    }
    if (previous) {
      queryClient.setQueryData(strategiesKey, { ...previous, data: remaining });
    }
    scheduleDelete({
      text: `Deleted “${strategy.name}”`,
      commit: () => deleteStrategy(strategy.id),
      restore: () => {
        if (previous) queryClient.setQueryData(strategiesKey, previous);
      },
    });
  };

  // Keep the current selection across list refetches (rename/clone/add all
  // invalidate the list) — only fall back to the first strategy when the
  // selected one no longer exists. Re-selecting the matched object keeps the
  // displayed name fresh after a rename. A strategy just created here wins
  // once the refetched list contains it.
  useEffect(() => {
    if (!strategies?.data?.length) return;
    if (pendingStrategyId) {
      const created = strategies.data.find(
        (s) => String(s.id) === pendingStrategyId
      );
      // The list hasn't caught up yet — leave the current selection alone
      // rather than bouncing it to the first strategy.
      if (!created) return;
      setPendingStrategyId(null);
      setSelectedStrategy(created);
      return;
    }
    const match = strategies.data.find((s) => s.id === selectedStrategy?.id);
    setSelectedStrategy(match ?? strategies.data[0]);
  }, [strategies, selectedStrategy?.id, setSelectedStrategy, pendingStrategyId]);

  const tiles = computing ? parseTileProgress(status?.message) : null;
  const progress = tiles ? tiles.done / tiles.total : computing ? 0 : null;
  const onlyOne = list.length < 2;

  return (
    <div>
      <ScenarioSegmentButton
        label="Strategy"
        icon="layers"
        value={selectedStrategy?.name ?? "Select a strategy"}
        tooltip={
          computing
            ? `Processing ${selectedStrategy?.name ?? ""}${
                tiles ? ` · ${tiles.done} of ${tiles.total} tiles` : ""
              }`
            : selectedStrategy?.name
        }
        meta={
          computing ? (
            <Box
              component="span"
              aria-live="polite"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.625, color: SB.accentMeta }}
            >
              <CircularProgress size={11} thickness={5} sx={{ color: SB.accent }} />
              {tiles ? `${tiles.done}/${tiles.total}` : null}
            </Box>
          ) : undefined
        }
        progress={progress}
        open={Boolean(anchorEl)}
        onClick={handleClick}
      />
      <ScenarioPopover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        title="Strategies"
        count={list.length}
        onNew={viewOnly ? undefined : () => setModalOpen(true)}
      >
        {listLoading && <StatusLine>Loading…</StatusLine>}
        {error && <StatusLine>Error: {error.message}</StatusLine>}
        {!listLoading && !error && list.length === 0 && (
          <ScenarioEmptyState
            icon="layers"
            title="No strategies"
            body="Create a strategy to hold the treatments you want to compare."
          />
        )}
        {list.length > 0 && (
          <ScenarioList role="listbox" ariaLabel="Strategy" maxHeight={360}>
            {list.map((strategy) => (
              <ScenarioRow
                key={strategy.id}
                selectable
                selected={strategy.id === selectedStrategy?.id}
                onSelect={() => handleStrategySelect(strategy)}
                name={strategy.name}
                actions={
                  viewOnly
                    ? []
                    : [{ icon: "edit", label: "Rename", onClick: () => handleEditStrategy(strategy) }]
                }
                menuItems={[
                  ...(viewOnly
                    ? []
                    : [
                        {
                          icon: "content_copy",
                          label: "Duplicate",
                          onClick: () => handleCloneStrategy(strategy.id),
                        },
                      ]),
                  {
                    icon: "download",
                    label: "Export",
                    onClick: () => handleExportStrategy(strategy.id),
                  },
                  ...(viewOnly
                    ? []
                    : [
                        {
                          icon: "delete",
                          label: "Delete strategy",
                          danger: true,
                          dividerAbove: true,
                          disabled: onlyOne,
                          title: onlyOne ? "A project needs at least one strategy" : undefined,
                          onClick: () => handleDeleteStrategy(strategy),
                        },
                      ]),
                ]}
              />
            ))}
          </ScenarioList>
        )}
      </ScenarioPopover>
      <UndoToast pending={pending} onUndo={undo} onDismiss={dismiss} />
      <AddStrategyModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        onAddStrategy={async (name: string) => {
          const response = await addStrategy(name);
          const newId = response?.data?.id;
          if (newId != null) setPendingStrategyId(String(newId));
        }}
      />
      <EditStrategyModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setStrategyToEdit(null);
        }}
        onEditStrategy={handleEditSubmit}
        initialName={strategyToEdit?.name || ""}
        initialDescription={strategyToEdit?.description || ""}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
